import React from "react";

import { Check, FolderTree, Loader2, Search, Sparkles, X } from "lucide-react";

import { useCanonicalCategoryMapping } from "@/hooks/useCanonicalCategoryMapping";
import type {
  CategorySuggestion,
  ProductType,
} from "@/hooks/useCanonicalCategoryMapping.types";
import { cn } from "@/lib/utils";

import { CategoryPath } from "../CategoryPath";
import { ConfidenceBadge } from "../ConfidenceBadge";
import { SaveBar } from "./SaveBar";

interface CanonicalCategoryMappingProps {
  selectedProductType: ProductType | null;
  onSave?: (canonicalCategoryId: string) => void;
  marketplace?: string;
  sourceQuery?: string | null;
  initialCategory?: CategorySuggestion | null;
}

export const CanonicalCategoryMapping: React.FC<
  CanonicalCategoryMappingProps
> = ({
  selectedProductType,
  onSave,
  marketplace,
  sourceQuery,
  initialCategory,
}) => {
  const {
    selectedCategory,
    searchQuery,
    setSearchQuery,
    isSearchMode,
    allSuggestions,
    searchResults,
    isSearching,
    suggestionsLoading,
    saveMutation,
    handleSelectSuggestion,
    handleClearSelection,
    handleConfirmMapping,
  } = useCanonicalCategoryMapping(
    selectedProductType,
    onSave,
    marketplace,
    sourceQuery,
    initialCategory,
  );

  if (!selectedProductType) return null;

  const listItems = isSearchMode ? searchResults : allSuggestions;
  const isLoading = isSearchMode ? isSearching : suggestionsLoading;

  return (
    <div
      data-testid="canonical-category-mapping"
      className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      {/* Header */}
      <div className="flex items-center gap-2 pt-1">
        <div className="p-1.5 rounded-md bg-violet-100 dark:bg-violet-900/50">
          <FolderTree className="w-4 h-4 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Canonical Category
          </h3>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Map &ldquo;
            {selectedProductType.displayName ?? selectedProductType.name}
            &rdquo; to a canonical category
          </p>
        </div>
      </div>

      {/* Unified picker */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Search input */}
        <div className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              data-testid="category-unified-search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search canonical categories, or choose from AI suggestions below…"
              className={cn(
                "w-full pl-8 py-1.5 text-sm rounded-lg",
                "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700",
                "text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500",
                "focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors",
                searchQuery ? "pr-8" : "pr-3",
              )}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Section label */}
        <div className="px-4 py-1.5 flex items-center gap-1.5 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800">
          {isSearchMode ? (
            <>
              <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                Search Results
              </span>
              {!isSearching && (
                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                  ({searchResults.length})
                </span>
              )}
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3 text-violet-400" />
              <span className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider">
                AI Suggestions
              </span>
            </>
          )}
        </div>

        {/* List */}
        <div className="max-h-[260px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-5 text-sm text-slate-400 dark:text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin text-violet-400 shrink-0" />
              {isSearchMode
                ? "Searching categories…"
                : "Loading AI suggestions…"}
            </div>
          ) : listItems.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-slate-400 dark:text-slate-500">
                {isSearchMode
                  ? "No categories found — try a different term"
                  : "No suggestions available — type to search manually"}
              </p>
            </div>
          ) : (
            listItems.map((item) => {
              const isSelected =
                selectedCategory?.categoryId === item.categoryId;
              const isMapped = !!item.mappedTo;
              return (
                <div
                  key={item.categoryId}
                  data-testid={`category-row-${item.categoryId}`}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 transition-colors",
                    isMapped
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer",
                    !isMapped && isSelected
                      ? "bg-violet-50 dark:bg-violet-950/30"
                      : "",
                    !isMapped && !isSelected
                      ? "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      : "",
                  )}
                  onClick={() => handleSelectSuggestion(item)}
                >
                  <div className="flex-1 min-w-0">
                    <CategoryPath path={item.path} />
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                      {isMapped
                        ? `Already mapped to ${item.mappedTo!.productType}`
                        : item.reason}
                    </p>
                  </div>

                  {isMapped ? (
                    <span
                      title={`This category already has an active mapping to "${item.mappedTo!.productType}" — edit that mapping instead of creating a new one`}
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border whitespace-nowrap shrink-0 bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400"
                    >
                      Already mapped
                    </span>
                  ) : (
                    <>
                      {item.confidence > 0 && item.confidence < 1.0 && (
                        <ConfidenceBadge confidence={item.confidence} />
                      )}
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded border whitespace-nowrap shrink-0 transition-colors",
                          isSelected
                            ? "bg-violet-100 dark:bg-violet-950/50 border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300"
                            : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400",
                        )}
                      >
                        {isSelected && <Check className="w-3 h-3" />}
                        {isSelected ? "Selected" : "Select"}
                      </span>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Save bar — appears once a category is selected */}
      {selectedCategory && (
        <SaveBar
          selectedProductType={selectedProductType}
          selectedCategory={selectedCategory}
          isPending={saveMutation.isPending}
          onClear={handleClearSelection}
          onConfirm={handleConfirmMapping}
        />
      )}
    </div>
  );
};
